import BoxWrap from "../box-wrap";
import CustomButton from "../custom-button";
import ForgotPasswordFields from "../fields/forgot-password";
import forgotPasswordValidation from "../../../validation/forgotPassword";
import React from "react";
import { Formik } from "formik";
import { showErrorAlert, showSuccessAlert } from "../alerts";
import { useForgotPassword } from "../../../api/auth";
import { useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";

/**
 * A form component for handling password recovery requests.
 * 
 * This component provides a form interface that:
 * - Collects user email for password reset
 * - Validates email input
 * - Handles password recovery request submission
 * - Provides success and error notifications
 * - Includes navigation options
 * 
 * @component
 * @example
 * ```tsx
 * <ForgotPasswordForm />
 * ```
 * 
 * @remarks
 * - Uses Formik for form state management
 * - Implements Material-UI components
 * - Handles form validation
 * - Provides user feedback
 * - Includes navigation controls
 * 
 * @returns {JSX.Element} A form component for password recovery
 */
const ForgotPasswordForm = () => {
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const forgotPasswordMutation = useForgotPassword();

  const initialValues = {
    email: "",
  };

  /**
   * Handles the form submission process for password recovery.
   * 
   * @param {any} values - The form values containing the user's email
   * @returns {Promise<void>} A promise that resolves when the recovery request is processed
   */
  const handleFormSubmit = async (values: any) => {
    try {
      const { email } = values;
      await forgotPasswordMutation.mutateAsync({ email });
      await showSuccessAlert("Eine E-Mail wurde erfolgreich gesendet!");
    } catch (err: any) {
      const { data } = err.response;
      showErrorAlert(data.message);
    }
  };
  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validationSchema={forgotPasswordValidation}
      validateOnBlur={false}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
      }) => (
        <form onSubmit={handleSubmit}>
          <ForgotPasswordFields
            isNonMobile={isNonMobile}
            values={values}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            handleChange={handleChange}
          />
          <BoxWrap
            hasBackground={false}
            display="flex"
            justifyContent="end"
            gap="20px"
            m="0px 15px"
            py={4}
          >
            <CustomButton
              label="Zurück zur Loginseite"
              color="inherit"
              handleOnClick={() => {
                navigate(-1);
              }}
            />
            <CustomButton
              type="submit"
              disabled={isSubmitting}
              label={isSubmitting ? "Bestätigen..." : "Bestätigen"}
            />
          </BoxWrap>
        </form>
      )}
    </Formik>
  );
};

export default ForgotPasswordForm;
